import { Component, useCallback, useState, type ErrorInfo, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Button, Card, PageHeader } from '@zarya/ui';
import { screenTitle, type RouteConsole } from '@app/navigation';
import { t } from '@app/i18n';
import { lastRequestID } from '@app/lastRequest';
import s from './TelaErrorBoundary.module.css';

/**
 * Limite de erro **por rota** (T2).
 *
 * O que ele evita é a tela branca: uma leitura acessória fora do contrato — uma
 * resposta **200** com forma inesperada — derruba o render da tela inteira, e o
 * console some sem dizer o quê nem onde. Foi o que aconteceu em Identidades, com
 * `identity-roles` devolvendo `{itens:[…]}` onde o contrato promete um array.
 *
 * O card responde as três perguntas que o relato precisa ter: **qual rota**
 * quebrou, **qual requisição** trouxe o dado (o `X-Request-Id` da última
 * resposta, que é por onde se acha a linha no log do backend) e **qual foi a
 * falha**. O rail e a topbar continuam de pé — o limite fica dentro do
 * `<Outlet />`, não em volta dele.
 */

interface LimitProps {
  rota: RouteConsole;
  onReiniciar: () => void;
  children: ReactNode;
}

interface LimitState {
  erro: Error | null;
  requestId: string | null;
}

function messageOf(erro: unknown): string {
  if (erro instanceof Error) return erro.message || erro.name;
  return String(erro);
}

class Limit extends Component<LimitProps, LimitState> {
  state: LimitState = { erro: null, requestId: null };

  static getDerivedStateFromError(erro: Error): LimitState {
    // Lido no instante da quebra: depois disso outra tela já pode ter pedido
    // alguma coisa e o `X-Request-Id` guardado deixaria de ser o desta.
    return { erro, requestId: lastRequestID() };
  }

  componentDidCatch(erro: Error, info: ErrorInfo): void {
    const { rota } = this.props;
    const requestId = this.state.requestId ?? lastRequestID();
    // `console.error` de propósito: `scripts/dirigir-tela.mjs` capta este canal,
    // então quem dirige a tela lê rota e request-id sem abrir o DevTools.
    console.error(
      `[tela] ${rota.href} (${rota.tela}) quebrou ao desenhar` +
        (requestId ? ` — request-id ${requestId}` : ' — sem request-id'),
      erro,
      info.componentStack,
    );
  }

  render(): ReactNode {
    const { erro, requestId } = this.state;
    const { rota, children } = this.props;

    if (!erro) return children;

    return (
      <>
        <PageHeader
          title={screenTitle(rota)}
          subtitle={t('shell.boundary.subtitle', { screen: rota.tela })}
          icon={rota.icon}
        />
        <Card title={t('shell.boundary.cardTitle')}>
          <div role="alert">
            {/* A frase cita o nome da tela no meio dela, e por isso o `{title}` é
                parâmetro do catálogo em vez de um `<strong>` fatiando a sentença:
                em inglês o nome não fica na mesma posição. */}
            <p className={s.explicacao}>
              {t('shell.boundary.explanation', { title: screenTitle(rota) })}
            </p>
            <dl className={s.dados}>
              <div className={s.linha}>
                <dt>{t('shell.boundary.route')}</dt>
                <dd>
                  <code>{rota.href}</code> · {rota.tela}
                </dd>
              </div>
              <div className={s.linha}>
                {/* O rótulo diz "último recebido", não "da falha", porque é o que
                    ele é: uma resposta sem `X-Request-Id` não apaga o da anterior,
                    então este id **pode** ser de outra chamada. Afirmar mais do que
                    sabemos mandaria quem investiga para a linha errada do log. */}
                <dt>{t('shell.boundary.lastRequestId')}</dt>
                <dd>
                  {requestId ? (
                    <>
                      <code data-testid="request-id">{requestId}</code>
                      <span className={s.ressalva}>{t('shell.boundary.requestIdCaveat')}</span>
                    </>
                  ) : (
                    <span className={s.ausente}>{t('shell.boundary.requestIdMissing')}</span>
                  )}
                </dd>
              </div>
              <div className={s.linha}>
                <dt>{t('shell.boundary.failure')}</dt>
                <dd>
                  <code>{messageOf(erro)}</code>
                </dd>
              </div>
            </dl>
            <p className={s.rodape}>{t('shell.boundary.footer')}</p>
          </div>
          <div className={s.acoes}>
            <Button variant="primary" icon="refresh" onClick={this.props.onReiniciar}>
              {t('common.ui.retry')}
            </Button>
            <Link className={s.voltar} to="/">
              {t('shell.boundary.backHome')}
            </Link>
          </div>
        </Card>
      </>
    );
  }
}

export interface ScreenErrorBoundaryProps {
  rota: RouteConsole;
  children: ReactNode;
}

/**
 * Envolve a tela de uma rota. O `key` combina rota e tentativa, então trocar de
 * rota **ou** pedir "tentar de novo" monta um limite novo — um limite que já
 * pegou erro não volta sozinho ao normal.
 *
 * "Tentar de novo" também **descarta o cache do react-query**: sem isso a tela
 * remontaria sobre o mesmo dado que a derrubou e quebraria de novo no mesmo
 * instante — um botão que promete e não pode cumprir. Descartado o cache, a
 * tentativa é de verdade: se o servidor voltou ao contrato a tela abre, se não
 * voltou o card reaparece, que é a resposta honesta.
 */
export function ScreenErrorBoundary({ rota, children }: ScreenErrorBoundaryProps) {
  const queryClient = useQueryClient();
  const [tentativa, setTentativa] = useState(0);

  const reiniciar = useCallback(() => {
    queryClient.clear();
    setTentativa((n) => n + 1);
  }, [queryClient]);

  return (
    <Limit key={`${rota.href}#${tentativa}`} rota={rota} onReiniciar={reiniciar}>
      {children}
    </Limit>
  );
}
